import React, { useEffect, useMemo } from 'react'
import { apolloClientPublic } from 'front-end/src/graphql/apolloClient'
import { SessionQuery, useSessionQuery } from './usePublicApiSession.codegen'
import { transitionAppStateToLoggedIn } from 'front-end/src/pages/auth/SessionHelpers'
import { useOrganisation } from 'front-end/src/public-api/hooks/useOrganisation'
import {
  adminAndManagerOrganisationRoles,
  OrganisationRoleEnum
} from 'types/Organisation'

type PublicApiSessionCtxType = {
  session: SessionQuery['session']
  refetch: () => void
  loading: boolean
  data: SessionQuery
  isAdmin: boolean
  userIsAdminOrManager: boolean
}

const PublicApiSessionCtx = React.createContext<PublicApiSessionCtxType>(
  null as unknown as PublicApiSessionCtxType
)

export const WithPublicApiSession: React.FC = ({ children }) => {
  const { data: orgData } = useOrganisation()
  const match = location.pathname.match(/authSuccessCallback\/(.*)/) // useParams hook cannot be used-doesn't work correctly before react-router-dom context is initialized
  const sid = match && match[1]

  const queryResult = useSessionQuery({
    variables: { sid },
    fetchPolicy: sid ? 'network-only' : 'cache-first',
    client: apolloClientPublic
  })
  const session = queryResult.data?.session

  useEffect(() => {
    if (session?.user && orgData.organisation) {
      transitionAppStateToLoggedIn(session, orgData.organisation)
    }
  }, [session, orgData])

  const ctx = useMemo(() => {
    const userIsAdminOrManager =
      adminAndManagerOrganisationRoles.includes(
        session?.user.organisation_role
      ) || false

    const isAdmin =
      session?.user.organisation_role === OrganisationRoleEnum.ADMIN || false

    return { session, ...queryResult, userIsAdminOrManager, isAdmin }
  }, [session, queryResult])

  return (
    <PublicApiSessionCtx.Provider value={ctx}>
      {children}
    </PublicApiSessionCtx.Provider>
  )
}

export function usePublicApiSession() {
  return React.useContext(PublicApiSessionCtx)
}
